import React from 'react';

import { Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { TourOfferPaymentScheduleTemplate } from '~/types/tours/tourOffer';

import { snakeCaseToStartCase } from '~/utils/stringUtils';

interface Props {
  templates: Array<TourOfferPaymentScheduleTemplate>;
}

// As the tour form labels svc-payment's charge fields.
function headingFor(field: string): string {
  if (field === 'due_date_value') {
    return 'Due days';
  }
  if (field === 'due_date_Type' || field === 'due_date_type') {
    return 'From booking/supplier';
  }
  return snakeCaseToStartCase(field);
}

function rangeOf(template: TourOfferPaymentScheduleTemplate): string | null {
  const range = template.leadTimeRange;
  if (!range) {
    return null;
  }
  return range.maxDays === null ? `${range.minDays}+ days` : `${range.minDays}–${range.maxDays} days`;
}

/** Each chosen template: what it is, and every charge it makes, as the tour form explains it. */
export default function NativeTourTemplateDetails({ templates }: Props) {
  if (templates.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {templates.map((template) => {
        const charges = template.paymentSchedules ?? [];
        const headings = [...new Set(charges.flatMap((charge) => Object.keys(charge)))];
        const range = rangeOf(template);
        return (
          <Paper key={template.id} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">{template.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {template.description}
                {range ? ` · Booked ${range} ahead` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {template.id}
              </Typography>
              {charges.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {headings.map((heading) => (
                        <TableCell key={heading}>{headingFor(heading)}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {charges.map((charge, index) => (
                      <TableRow key={index}>
                        {headings.map((heading) => (
                          <TableCell key={heading}>{String(charge[heading] ?? '')}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
